import { useEffect, useRef, useState } from 'react';
import { APITypes, Dynamo } from '../../../primary-record-types';
import { useNestedNavigation } from '../../../utils/appNavigationNames';
import useFeatureToggles from '../../../hooks/api/useFeatureToggles';
import usePayments from '../../../hooks/api/usePayments';
import useUser, { isUserPendingRegistration } from '../../../hooks/api/useUser';
import { useCollaboratorInvitations } from '../../../hooks/api/useCollaboratorInvitations';
import useInboundTransferRequests from '../../../hooks/api/useInboundTransferRequests';
import useOrganization from '../../../hooks/api/useOrganization';
import { useExplicitAppType } from '../../../hooks/useAppType';
import { useCapturedUrlUserType, useUrlRegistrationCode } from '../../../hooks/useUrlAppParams';
import { useMyOrganizationClientInvitations } from '../../../hooks/api/useMyOrganizationClientInvitations';
import useRecordEvent from '../../../hooks/useRecordEvent';

export type OnboardingStage = 'user-registration' | 'professional-org-selection' | 'consumer-app-welcome';
export type FullUserOnboardingLifecycleStage = OnboardingStage | 'onboarding-complete' | 'initializing';

export type RegisterUserArgs = {
    user: APITypes.userService.UpdateUserBody;
    intents?: Dynamo.UserIntent[];
    type?: Dynamo.UserType;
};

export type UseUserOnboardingFlowReturnValue = {
    fullUserOnboardingLifecycleStage: FullUserOnboardingLifecycleStage;
    /**
     * User registration form to collect basic preliminary information.
     */
    userRegistration: {
        /**
         * Boolean to determine if user registration form is loading.
         */
        isLoadingUserRegistration: boolean;
        /**
         * Potentially default the user type.  We can intelligently default depending on if the user has a pending org invite.
         */
        defaultUserType: Dynamo.UserType | null;
        /**
         * Callback to register a user.
         */
        onRegisterUser: (args: RegisterUserArgs) => void;
        /**
         * If the user is registering and has an org invite, we can skip the user intent screen.
         */
        skipUserIntents?: boolean;
    };
    /**
     * Welcome screen to display after registration is complete if using app in a consumer context.
     */
    consumerAppWelcome: {
        /**
         * Boolean to determine if the consumer app welcome screen is loading.
         */
        isLoadingConsumerAppWelcome: boolean;
        /**
         * Callback to continue to the next screen.
         */
        onContinue: () => void;
    };
    /**
     * If a professional user, the user must be associated with an organization.  This gate will either prompt a creation, accepting an org invite, or waiting for an org invite to be accepted.
     */
    proAppOrganizationGate: {
        /**
         * Boolean to determine if the pro app organization gate is loading.
         */
        isLoadingProAppOrganizationGate: boolean;
        /**
         * Callback to create an organization.
         */
        onCreateOrganization: (organization: APITypes.organizationService.CreateOrganizationRequest) => void;
    };
};

type EvaluateUserOnboardingLifecycleStageArgs = {
    user: APITypes.userService.MeResponse | 'loading';
    userLaunchedAppUnregistered: boolean;
    userHasProgressedPastWelcomeScreen: boolean;
    isLoadingMyOrganization: boolean;
    myOrganization: APITypes.organizationService.GetOrganizationMeResponse | null;
    /**
     * If the user has any client invites for their organization at any point since launching the app
     */
    userHasHadOrgClientInvites: boolean | 'loading';
};

const evaluateOnboardingLifecycleStage = ({
    user,
    userLaunchedAppUnregistered,
    userHasProgressedPastWelcomeScreen,
    myOrganization,
    isLoadingMyOrganization,
    userHasHadOrgClientInvites,
}: EvaluateUserOnboardingLifecycleStageArgs): FullUserOnboardingLifecycleStage => {
    if (user === 'loading' || isLoadingMyOrganization || userHasHadOrgClientInvites === 'loading') {
        return 'initializing';
    } else if (isUserPendingRegistration(user)) {
        return 'user-registration';
    } else if (user.type === 'professional' && !myOrganization) {
        return 'professional-org-selection';
    } else if (
        userLaunchedAppUnregistered &&
        !userHasProgressedPastWelcomeScreen &&
        user.type !== 'professional' &&
        !userHasHadOrgClientInvites
    ) {
        return 'consumer-app-welcome';
    } else {
        return 'onboarding-complete';
    }
};

/**
 * Automatically apply the registration code if found in the url.
 */
const useAutoApplyRegistrationCode = () => {
    const didApplyRef = useRef(false);
    const appType = useExplicitAppType();
    const recordEvent = useRecordEvent();
    const { autoApplyRegistrationCode, paymentStatus, isLoadingPaymentStatus, isAutoApplyingRegistrationCode } =
        usePayments({
            onAutoApplyRegistrationCodeSuccess: (code) => {
                recordEvent('registrationCodeApplied', {
                    appliedRegistrationCode: code,
                });
            },
        });
    const urlRegistrationCode = useUrlRegistrationCode();
    useEffect(() => {
        if (
            urlRegistrationCode &&
            !didApplyRef.current &&
            !paymentStatus?.entitlements?.length &&
            !isLoadingPaymentStatus &&
            appType === 'consumer'
        ) {
            didApplyRef.current = true;
            autoApplyRegistrationCode({ code: urlRegistrationCode });
        }
    }, [
        urlRegistrationCode,
        autoApplyRegistrationCode,
        paymentStatus,
        isAutoApplyingRegistrationCode,
        isLoadingPaymentStatus,
        appType,
    ]);
};

/**
 * Hook to manage the complex logic user registration flow.
 *
 * This hook is intended to be used in the root of the app to enforce user registration and manage the different user states of:
 * * User not registered
 * * User registered but not set intent
 * * User registered and set intent
 * * Progressing to a default screen after registration is complete
 */
export const useUserOnboardingFlow = (): UseUserOnboardingFlowReturnValue => {
    // Load feature toggles once on app load; this makes consumers faster
    useFeatureToggles();
    useAutoApplyRegistrationCode();

    const nestedNavigation = useNestedNavigation();
    const { user } = useUser();
    const { isLoadingInvitations, invitations } = useCollaboratorInvitations({});
    const { createOrganization, isCreatingOrganization, myOrganization, isLoadingMyOrganization } = useOrganization({});
    const { isLoadingInboundTransferRequests, inboundTransferRequests } = useInboundTransferRequests();
    const urlUserType = useCapturedUrlUserType();

    const pendingInvitation = invitations?.[0];
    const pendingTransferRequest = inboundTransferRequests?.[0];

    const [userHasProgressedPastWelcomeScreen, setUserHasProgressedPastWelcomeScreen] = useState<boolean>(false);

    // Determine if the user has launched the app but not registered yet
    const [userLaunchedAppUnregistered, setUserLaunchedAppUnregistered] = useState<boolean | null>(null);
    useEffect(() => {
        if (user === 'loading' || userLaunchedAppUnregistered !== null) {
            return;
        }

        setUserLaunchedAppUnregistered(user.promptRegistration);
    }, [user, userLaunchedAppUnregistered]);

    const { updateUser, isUpdatingUser } = useUser({
        noUpdateMessage: true,
    });
    const { clientInvitations, isLoadingClientInvitations } = useMyOrganizationClientInvitations({});
    const [userHasHadOrgClientInvites, setUserHasHadOrgClientInvites] = useState<boolean | 'loading'>('loading');
    useEffect(() => {
        if (clientInvitations && userHasHadOrgClientInvites === 'loading' && !isLoadingClientInvitations) {
            setUserHasHadOrgClientInvites(!!clientInvitations.length);
        }
    }, [clientInvitations, userHasHadOrgClientInvites, isLoadingClientInvitations]);

    const lifecycleStage = evaluateOnboardingLifecycleStage({
        user,
        userLaunchedAppUnregistered: userLaunchedAppUnregistered ?? true,
        userHasProgressedPastWelcomeScreen,
        myOrganization,
        isLoadingMyOrganization,
        userHasHadOrgClientInvites,
    });

    return {
        fullUserOnboardingLifecycleStage: lifecycleStage,
        userRegistration: {
            isLoadingUserRegistration: user === 'loading' || isUpdatingUser,
            defaultUserType: user !== 'loading' && user.type ? user.type : urlUserType,
            skipUserIntents: userHasHadOrgClientInvites === true,
            onRegisterUser: ({ user, intents, type }) => {
                const updatedUserIntents: Set<Dynamo.UserIntent> = new Set();
                if (user.user.userIntents) user.user.userIntents.forEach((intent) => updatedUserIntents.add(intent));
                if (intents) intents.forEach((intent) => updatedUserIntents.add(intent));
                updateUser({
                    user: {
                        ...user.user,
                        userIntents: [...updatedUserIntents],
                        type,
                    },
                    avatar: {
                        type: 'not-modified',
                    },
                });
            },
        },
        consumerAppWelcome: {
            isLoadingConsumerAppWelcome: user === 'loading' || isLoadingInvitations || isLoadingInboundTransferRequests,
            onContinue: () => {
                setUserHasProgressedPastWelcomeScreen(true);

                if (pendingTransferRequest) {
                    nestedNavigation.nestedProfileNavigation('transfer-request-response', {
                        transferProfileId: pendingTransferRequest?.profile.medicalProfileId,
                        transferRequestId: pendingTransferRequest.medicalProfileOwnershipTransferId,
                    });
                } else if (pendingInvitation) {
                    nestedNavigation.nestedProfileNavigation('invite', {
                        profileId: pendingInvitation?.medicalProfileId,
                        invitationId: pendingInvitation?.medicalProfileCollaboratorInviteId,
                    });
                } else {
                    nestedNavigation.nestedProfileNavigation('root', {
                        isRegistration: 'true',
                    });
                }
            },
        },
        proAppOrganizationGate: {
            isLoadingProAppOrganizationGate: user === 'loading' || isCreatingOrganization,
            onCreateOrganization: createOrganization,
        },
    };
};
